(function () {
  'use strict';

  /**
   * Additive ARIA + keyboard handler for <tabs-object> widgets.
   *
   * What the compiled bundle already does:
   *   - Handles click, updates a visual indicator, adds/removes "is-active"
   *     class on the active <button data-tab>, calls an optional callback.
   *
   * What this script adds (without replacing anything):
   *   1. Roving tabindex + aria-selected kept in sync with the "is-active" class
   *      via MutationObserver (so it works whether tabs are activated by click,
   *      by our keyboard handler, or programmatically by other JS).
   *   2. Arrow / Home / End keyboard navigation between tabs.
   *   3. Enter / Space activation (the existing code listens for "click", which
   *      browsers already fire on Enter for buttons; Space is also native for
   *      <button> — so this is belt-and-suspenders for completeness).
   *
   * The script is intentionally dependency-free and runs as a one-shot IIFE so
   * it ships with no build step and attaches to any <tabs-object> present now
   * or injected later via MutationObserver.
   */

  var ACTIVE_CLASS = 'is-active';

  function getTabs(tabsObject) {
    return Array.prototype.slice.call(
      tabsObject.querySelectorAll('[role="tab"]')
    );
  }

  /**
   * Sync aria-selected and tabindex on all tabs inside a <tabs-object> so the
   * tab with is-active class is "selected" and all others are not.
   * Also updates aria-labelledby on a shared panel (single-panel filter
   * widgets like FAQs) to reflect the currently active tab.
   */
  function syncAriaState(tabsObject) {
    var tabs = getTabs(tabsObject);
    var activeIndex = -1;
    tabs.forEach(function (tab, i) {
      if (tab.classList.contains(ACTIVE_CLASS)) {
        activeIndex = i;
      }
    });
    // If nothing has is-active yet, treat the first tab as selected
    if (activeIndex === -1) activeIndex = 0;

    var activeTabId = null;
    tabs.forEach(function (tab, i) {
      var selected = i === activeIndex;
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      tab.setAttribute('tabindex', selected ? '0' : '-1');
      if (selected) activeTabId = tab.id;
    });

    // If all tabs share one panel, keep its aria-labelledby pointing to the
    // active tab so the announced relationship stays accurate.
    if (activeTabId && tabs.length > 0) {
      var firstControls = tabs[0].getAttribute('aria-controls');
      var allSamePanel = tabs.every(function (tab) {
        return tab.getAttribute('aria-controls') === firstControls;
      });
      if (allSamePanel && firstControls) {
        var panel = document.getElementById(firstControls);
        if (panel) {
          panel.setAttribute('aria-labelledby', activeTabId);
        }
      }
    }
  }

  /**
   * Move focus to a tab by index, wrapping at boundaries.
   */
  function focusTab(tabs, index) {
    var target = tabs[index];
    if (target) {
      target.focus();
      // Do NOT activate on Arrow key alone — user must press Enter/Space.
      // (This follows the APG "manual activation" pattern, which is safer for
      // carousels/FAQs that may have expensive render on activation.)
    }
  }

  function onKeyDown(e) {
    var tab = e.target;
    if (!tab || tab.getAttribute('role') !== 'tab') return;

    var tabsObject = tab.closest('tabs-object');
    if (!tabsObject) return;

    var tabs = getTabs(tabsObject);
    var currentIndex = tabs.indexOf(tab);
    if (currentIndex === -1) return;

    var key = e.key;

    if (key === 'ArrowLeft' || key === 'ArrowUp') {
      e.preventDefault();
      var prev = (currentIndex - 1 + tabs.length) % tabs.length;
      focusTab(tabs, prev);
    } else if (key === 'ArrowRight' || key === 'ArrowDown') {
      e.preventDefault();
      var next = (currentIndex + 1) % tabs.length;
      focusTab(tabs, next);
    } else if (key === 'Home') {
      e.preventDefault();
      focusTab(tabs, 0);
    } else if (key === 'End') {
      e.preventDefault();
      focusTab(tabs, tabs.length - 1);
    } else if (key === 'Enter' || key === ' ') {
      // Browsers already fire click on Enter/Space for <button>; this is a
      // no-op guard to ensure focus is on the correct tab after activation.
      e.preventDefault();
      tab.click();
    }
  }

  /**
   * Observe class mutations on each tab button so we can keep aria-selected
   * and tabindex in sync whenever the existing JS adds/removes is-active.
   */
  function observeTabsObject(tabsObject) {
    var tabs = getTabs(tabsObject);
    if (!tabs.length) return;

    // Initial sync
    syncAriaState(tabsObject);

    if (!('MutationObserver' in window)) return;

    var mo = new MutationObserver(function () {
      syncAriaState(tabsObject);
    });

    tabs.forEach(function (tab) {
      mo.observe(tab, { attributes: true, attributeFilter: ['class'] });
    });
  }

  /**
   * Attach to all <tabs-object> elements currently in the DOM and any added
   * later (e.g. Shopify section live-preview injection).
   */
  function attachToTabsObject(el) {
    if (el.tagName && el.tagName.toLowerCase() === 'tabs-object') {
      observeTabsObject(el);
    }
    // Also check descendants (e.g. when a parent container is injected)
    var nested = el.querySelectorAll ? el.querySelectorAll('tabs-object') : [];
    Array.prototype.forEach.call(nested, observeTabsObject);
  }

  function init() {
    // Keyboard handler — delegated from document so it covers dynamically
    // injected widgets too.
    document.addEventListener('keydown', onKeyDown);

    // Attach aria-state observers to all current <tabs-object> instances.
    document.querySelectorAll('tabs-object').forEach(observeTabsObject);

    // Watch for future <tabs-object> insertions.
    if ('MutationObserver' in window) {
      new MutationObserver(function (mutations) {
        mutations.forEach(function (mutation) {
          mutation.addedNodes.forEach(function (node) {
            if (node.nodeType !== 1) return;
            attachToTabsObject(node);
          });
        });
      }).observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
